import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { API } from "../../api/api";

interface ApiKeyInfo {
  key_id: string;
  name: string | null;
  result_access_level: string;
  scopes: string[] | null;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string | null;
  expires_at: string | null;
  key_prefix: string;
}

interface ApiKeyCreated extends ApiKeyInfo {
  raw_key: string;
}

export default function ApiKeys() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [newKeyName, setNewKeyName] = useState("Default API Key");
  const [expiryDays, setExpiryDays] = useState<number | "">("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: keys, isLoading } = useQuery<ApiKeyInfo[]>({
    queryKey: ["api-keys"],
    queryFn: async () => {
      const r = await fetch(`${API}/auth/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    enabled: !!token,
    staleTime: 30_000,
    gcTime: 60_000,
  });

  const createKey = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API}/auth/api-keys`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: newKeyName,
          expires_in_days: expiryDays || null,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json() as Promise<ApiKeyCreated>;
    },
    onSuccess: (data) => {
      setCreatedKey(data.raw_key);
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  const revokeKey = useMutation({
    mutationFn: async (keyId: string) => {
      const r = await fetch(`${API}/auth/api-keys/${keyId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  const copyKey = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-h3 font-bold text-text-primary mb-2">API Keys</h2>
      <p className="text-body-sm text-text-secondary mb-6">
        API keys allow programmatic access to the TotalView API. Use them in scripts,
        CI/CD pipelines, or custom integrations.
      </p>

      {/* Created key alert — shown once after creation */}
      {createdKey && (
        <div className="mb-6 p-4 rounded-lg border border-green-500/30 bg-green-500/10">
          <p className="text-body-sm font-semibold text-green-400 mb-2">
            API key created — copy it now, it won't be shown again
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 rounded bg-surface-900 text-text-primary font-mono text-body-sm break-all">
              {createdKey}
            </code>
            <button onClick={copyKey} className="btn-secondary px-3 py-2 text-body-sm whitespace-nowrap">
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <p className="mt-3 text-caption text-text-muted">
            Usage: <code className="text-quantum-400">curl -H "X-API-Key: {createdKey.slice(0, 15)}..." {API}/projects</code>
          </p>
          <button
            onClick={() => setCreatedKey(null)}
            className="mt-2 text-caption text-text-muted hover:text-text-primary"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Create new key */}
      <div className="mb-8 p-4 rounded-lg border border-border bg-surface-700">
        <h3 className="text-body font-semibold text-text-primary mb-3">Create New Key</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="input-label">Key Name</label>
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              className="input w-full"
              placeholder="e.g., Data Pipeline, CI/CD, Agent"
            />
          </div>
          <div className="w-40">
            <label className="input-label">Expires (days)</label>
            <input
              type="number"
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value ? parseInt(e.target.value) : "")}
              className="input w-full"
              placeholder="Never"
              min={1}
            />
          </div>
          <button
            onClick={() => createKey.mutate()}
            disabled={createKey.isPending || !newKeyName.trim()}
            className="btn-primary px-4 py-2 text-body-sm disabled:opacity-50"
          >
            {createKey.isPending ? "Creating..." : "Create Key"}
          </button>
        </div>
        {createKey.isError && (
          <p className="mt-2 text-caption text-health-400">{(createKey.error as Error).message}</p>
        )}
      </div>

      {/* Existing keys */}
      <h3 className="text-body font-semibold text-text-primary mb-3">Your Keys</h3>

      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-16 bg-surface-700 rounded-lg animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && (!keys || keys.length === 0) && (
        <div className="p-6 rounded-lg border border-border bg-surface-700 text-center">
          <p className="text-body-sm text-text-muted">No API keys yet. Create one above.</p>
        </div>
      )}

      {keys && keys.length > 0 && (
        <div className="space-y-3">
          {keys.map((key) => (
            <div
              key={key.key_id}
              className={`flex items-center justify-between p-4 rounded-lg border ${
                key.is_active ? "border-border bg-surface-700" : "border-border/50 bg-surface-700/50 opacity-60"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-body-sm font-medium text-text-primary">
                    {key.name || "Unnamed Key"}
                  </span>
                  <code className="text-caption text-text-muted font-mono">{key.key_prefix}...</code>
                  {!key.is_active && (
                    <span className="px-2 py-0.5 text-[10px] rounded bg-health-500/20 text-health-400 font-medium">
                      Revoked
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-1 text-caption text-text-muted">
                  <span>Created {key.created_at ? new Date(key.created_at).toLocaleDateString() : "—"}</span>
                  {key.last_used_at && (
                    <span>Last used {new Date(key.last_used_at).toLocaleDateString()}</span>
                  )}
                  {key.expires_at && (
                    <span>
                      Expires {new Date(key.expires_at).toLocaleDateString()}
                    </span>
                  )}
                  {!key.expires_at && key.is_active && (
                    <span>No expiry</span>
                  )}
                </div>
              </div>
              {key.is_active && (
                <button
                  onClick={() => {
                    if (window.confirm(`Revoke key "${key.name}"? This cannot be undone.`)) {
                      revokeKey.mutate(key.key_id);
                    }
                  }}
                  className="text-body-sm text-health-400 hover:text-health-300 px-3 py-1"
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Usage docs */}
      <div className="mt-8 p-4 rounded-lg border border-border bg-surface-700">
        <h3 className="text-body font-semibold text-text-primary mb-2">Quick Start</h3>
        <div className="space-y-3 text-body-sm text-text-secondary">
          <div>
            <p className="font-medium text-text-primary">List your projects:</p>
            <code className="block mt-1 px-3 py-2 rounded bg-surface-900 text-quantum-400 text-caption font-mono">
              curl -H "X-API-Key: YOUR_KEY" {API}/projects
            </code>
          </div>
          <div>
            <p className="font-medium text-text-primary">Queue a model run:</p>
            <code className="block mt-1 px-3 py-2 rounded bg-surface-900 text-quantum-400 text-caption font-mono">
              curl -X POST -H "X-API-Key: YOUR_KEY" -H "Content-Type: application/json" \<br />
              &nbsp;&nbsp;{API}/projects/PROJECT_ID/queue -d '{"{}"}'
            </code>
          </div>
        </div>
      </div>
    </div>
  );
}
